import {
  allowsChannelNotification,
  type ChannelNotificationMode,
} from './notification_policy.helpers';

/**
 * Pure decisions for Connect push notifications (mentions and direct
 * messages routed through the platform notification pipeline). No Convex
 * imports, so the policy is unit-testable and shared with the producer.
 */

/** At most one push per (channel, recipient) in this window. */
export const CHANNEL_PUSH_WINDOW_MS = 3 * 60_000;
/** Bound on recipients one message can push to. */
export const CHANNEL_PUSH_MAX_RECIPIENTS = 50;
const PREVIEW_MAX = 140;

export type ChannelPushKind = 'mention' | 'dm';

/**
 * Notification dedupe key: a burst of mentions or DMs in one chat collapses
 * into a single push per recipient per window, so an active conversation the
 * recipient isn't watching never turns into a stream of alerts.
 */
export function channelPushDedupeKey(
  channelId: string,
  recipientUserId: string,
  now: number
): string {
  const bucket = Math.floor(now / CHANNEL_PUSH_WINDOW_MS);
  return `channel-push:${channelId}:${recipientUserId}:${bucket}`;
}

/**
 * Whether one recipient should get a push for this message. Every Connect
 * push is personal (a mention, or a DM addressed to them), so it is treated
 * as a mention by the channel notification mode: `nothing` and an active
 * mute suppress it, and so does having the chat open right now.
 */
export function shouldPushChannelMessage(input: {
  recipientUserId: string;
  authorUserId: string;
  notificationMode?: ChannelNotificationMode;
  mutedUntil?: number | null;
  isViewingChannel: boolean;
  now: number;
}): boolean {
  if (input.recipientUserId === input.authorUserId) return false;
  if (input.isViewingChannel) return false;
  return allowsChannelNotification({
    notificationMode: input.notificationMode,
    mutedUntil: input.mutedUntil,
    isMention: true,
    now: input.now,
  });
}

/** Distinct recipients, author excluded, capped. */
export function channelPushRecipients<T extends string>(
  recipientUserIds: readonly T[],
  authorUserId: string
): T[] {
  return [...new Set(recipientUserIds)]
    .filter((userId) => userId !== authorUserId)
    .slice(0, CHANNEL_PUSH_MAX_RECIPIENTS);
}

/**
 * DM recipients: every other member of the (group) DM except those already
 * notified for an @mention of the same message.
 */
export function directMessageRecipients<T extends string>(input: {
  memberUserIds: readonly T[];
  authorUserId: string;
  mentionedUserIds: readonly string[];
}): T[] {
  const mentioned = new Set(input.mentionedUserIds);
  return channelPushRecipients(
    input.memberUserIds.filter((userId) => !mentioned.has(userId)),
    input.authorUserId
  );
}

/** Plain-text preview: mention markers and file links reduced to labels. */
export function previewChannelMessageBody(body: string): string | undefined {
  const cleaned = body
    .replace(/\[@([^\]]+)\]\((?:user|imai|broadcast):[^)]+\)/g, '@$1')
    .replace(/\[([^\]]+)\]\((?:file|vfs):[^)]+\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return undefined;
  return cleaned.length > PREVIEW_MAX
    ? `${cleaned.slice(0, PREVIEW_MAX - 1).trimEnd()}…`
    : cleaned;
}
